import React, { useEffect, useRef, useState } from 'react'
import { Popup, Label } from 'semantic-ui-react'
import { Set } from 'immutable'

import EasyToClickInlineButton from 'components/EasyToClickInlineButton'
import CustomerTagCreateUpdateDeletePopupContent from 'organisms/CustomerTagCreateUpdateDeletePopupContent'
import EasyToClickIcon from 'components/EasyToClickIcon'

const defaultSelectedTags = []

/**
 * @typedef CustomerTag
 * @property {number} id
 * @property {string} name
 *
 * @param {object} props
 * @param {CustomerTag[]} [props.selectedTags = []]
 * @param {CustomerTag[]} props.allTags
 * @param {boolean} [props.loading]
 * @param {function} [props.onChange]
 * @param {function} [props.onAddTag]
 * @param {function} [props.onRemoveTag]
 * @param {function} props.onCreateTag
 * @param {function} props.onModifyTag
 * @param {function} props.onDeleteTag
 */
export default function CustomerTagInput({
  selectedTags = defaultSelectedTags,
  allTags,
  loading,
  onChange = noop,
  onAddTag = noop,
  onRemoveTag = noop,
  onCreateTag,
  onModifyTag,
  onDeleteTag,
}) {
  const [selectedTagSetState, setSelectedTagSetState] = useState(Set(selectedTags))
  const previousSelectedTagSetStateRef = useRef(null)
  useEffect(() => {
    previousSelectedTagSetStateRef.current = selectedTagSetState
  }, [selectedTagSetState])

  useEffect(() => {
    onChange(selectedTagSetState.toArray())
  }, [onChange, selectedTagSetState])

  const [open, setOpen] = useState(false)

  function handleRemoveTag(tag) {
    setSelectedTagSetState((tagSet) => tagSet.delete(tag))
    onRemoveTag(tag)
  }

  useEffect(() => {
    const newSet = Set(selectedTags)
    if (!newSet.equals(previousSelectedTagSetStateRef.current)) {
      setSelectedTagSetState(newSet)
    }
  }, [selectedTags])

  return (
    <React.Fragment>
      <TagLabels customerTags={selectedTagSetState.toArray()} onRemoveTag={handleRemoveTag} />
      <Popup
        on="click"
        trigger={
          <EasyToClickInlineButton className="ui primary basic button tiny">
            <i className="icon far fa-plus" />
            Add tag
          </EasyToClickInlineButton>
        }
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
        className="p0 w-full"
        style={{
          // remove the min-width: max-content; that'll cause max-width to not take place
          minWidth: 'unset',
        }}
        wide
        basic
        position="right center"
      >
        <CustomerTagCreateUpdateDeletePopupContent
          loading={loading}
          allTags={allTags}
          defaultSelectedTagSet={selectedTagSetState}
          onChange={setSelectedTagSetState}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          onCreateTag={onCreateTag}
          onModifyTag={onModifyTag}
          onDeleteTag={onDeleteTag}
        />
      </Popup>
    </React.Fragment>
  )
}

function TagLabels({ customerTags = [], onRemoveTag }) {
  return (
    <React.Fragment>
      {customerTags.map((tag) => (
        <TagWithRemoveIcon key={tag.name} name={tag.name} onRemoveTag={() => onRemoveTag(tag)} />
      ))}
    </React.Fragment>
  )
}

/**
 * @typedef {object} TagProps
 * @property {string} name
 * @property {function} onRemoveTag
 *
 * @param {TagProps} props
 */
function TagWithRemoveIcon({ name, onRemoveTag }) {
  return (
    <Label basic style={{ marginBottom: 4, marginRight: 8, marginLeft: 0 }}>
      <span style={{ wordWrap: 'break-word' }}>{name}</span>
      <span className="ml-1">
        <EasyToClickIcon className="link grey icon fas fa-xmark" onClick={onRemoveTag} />
      </span>
    </Label>
  )
}

function noop() {}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/CustomerTagInput/index.js